import {
  SifaTeilnahme,
  SifaUnterweisung,
  UnterweisungAbschnitt,
  UnterweisungAntwort,
  UnterweisungFrage,
  UnterweisungStatus,
} from '../../services/sifa/types';
import { medienPfade, pfadImOrdner, VORLAGEN_ORDNER } from '../../services/sifa/vorlagenLogic';

/**
 * Die Regeln der Unterweisungs-Oberflaeche (Innendienst) — ohne React, ohne
 * DOM, ohne Supabase.
 *
 * WARUM hier: das Projekt hat keine jsdom-Testumgebung, Komponenten sind
 * nicht pruefbar. Es gilt „duenner Container, Logik in Hooks + pure
 * Funktionen". Alles, was in dieser Oberflaeche eine ENTSCHEIDUNG trifft —
 * darf der Kurs aktiv werden, welche Teilnahmen sind abrechenbar, wie lautet
 * der Kundenlink —, steht deshalb hier und in unterweisungModulLogic.test.ts.
 *
 * Die FACHLICHEN Regeln (Auswertung der Antworten, Faelligkeit, Buchung,
 * Abgleich „3 von 4") liegen in src/services/sifa/*. Dieses Modul ergaenzt
 * sie um das, was nur die Bedienung angeht.
 */

// ─── Kurs (Entwurf) ─────────────────────────────────────────────────────────

/** Die Felder, die der Editor haelt — ein Ausschnitt der Unterweisung. */
export type UnterweisungEntwurf = Pick<
  SifaUnterweisung,
  'titel' | 'beschreibung' | 'inhalt' | 'fragen' | 'bestehenProzent' | 'gueltigMonate' | 'dauerMin'
>;

export const VORGABE_BESTEHEN_PROZENT = 80;
export const VORGABE_GUELTIG_MONATE = 12;

/** Der Entwurf, mit dem ein neuer Kurs angelegt wird. */
export const leererEntwurf = (): UnterweisungEntwurf => ({
  titel: '',
  beschreibung: '',
  inhalt: [],
  fragen: [],
  bestehenProzent: VORGABE_BESTEHEN_PROZENT,
  gueltigMonate: VORGABE_GUELTIG_MONATE,
  dauerMin: null,
});

export const entwurfAus = (u: SifaUnterweisung): UnterweisungEntwurf => ({
  titel: u.titel || '',
  beschreibung: u.beschreibung || '',
  inhalt: (u.inhalt || []).map((a) => ({ ...a })),
  fragen: (u.fragen || []).map((f) => ({ ...f, antworten: (f.antworten || []).map((a) => ({ ...a })) })),
  bestehenProzent: Number.isFinite(u.bestehenProzent) ? u.bestehenProzent : VORGABE_BESTEHEN_PROZENT,
  gueltigMonate: Number.isFinite(u.gueltigMonate) ? u.gueltigMonate : VORGABE_GUELTIG_MONATE,
  dauerMin: u.dauerMin ?? null,
});

/** Neue Bausteine. Die `id` kommt von aussen (crypto.randomUUID), damit die Funktionen pur bleiben. */
export const neuerAbschnitt = (id: string): UnterweisungAbschnitt => ({ id, titel: '', text: '', bildPfad: null, videoPfad: null });

/** Auswahl „Stellen nach": null = am Ende, sonst die Abschnitts-Id. */
export const AM_ENDE = '';

/**
 * Die `abschnittId` einer Frage, die im Entwurf noch etwas bedeutet: nur,
 * wenn es den Abschnitt gibt. Ein geloeschter Abschnitt nimmt seine Fragen
 * nicht mit, sie rutschen ans Ende.
 */
export const gueltigeAbschnittId = (abschnittId: string | null | undefined, inhalt: UnterweisungAbschnitt[]): string | null => {
  const id = (abschnittId || '').trim();
  return id && inhalt.some((a) => a.id === id) ? id : null;
};
export const neueAntwort = (id: string, richtig = false): UnterweisungAntwort => ({ id, text: '', richtig });
export const neueFrage = (id: string, antwortIds: string[]): UnterweisungFrage => ({
  id,
  frage: '',
  antworten: antwortIds.map((aid, i) => neueAntwort(aid, i === 0)),
});

/**
 * Medienpfade, auf die der gespeicherte Stand `nachher` nicht mehr zeigt,
 * `vorher` aber noch (Bild/Video ersetzt oder entfernt). Der Editor loescht
 * sie ERST nach dem erfolgreichen Speichern aus dem Bucket — ein „Aenderungen
 * verwerfen" davor darf nicht auf eine geloeschte Datei zeigen. Nur Pfade
 * mit drei Segmenten (Medien-Bucket Nr. 84); alte zweisegmentige Pfade aus
 * dem privaten Bucket bleiben unangetastet.
 *
 * `ordner` (Nr. 141): nur Pfade, die diesem Eigentuemer gehoeren
 * (pfadImOrdner) — der Projekt-Editor uebergibt die projectId, der
 * Vorlagen-Editor `vorlagen/<id>`. Ohne `ordner` wie bisher, aber Pfade
 * unter `vorlagen/` sind IMMER ausgeschlossen: eine Kundenkopie zeigt auf die
 * Dateien der Vorlage, und wer dort einen Abschnitt entfernt, darf nicht das
 * Video aller anderen Kunden loeschen.
 */
export const verwaistePfade = (
  vorher: Pick<SifaUnterweisung, 'inhalt'>,
  nachher: Pick<SifaUnterweisung, 'inhalt'>,
  ordner?: string,
): string[] => {
  const bleibt = new Set(medienPfade(nachher.inhalt || []));
  return medienPfade(vorher.inhalt || []).filter((p) => {
    if (bleibt.has(p)) return false;
    if (ordner !== undefined) return pfadImOrdner(p, ordner);
    return p.split('/').length === 3 && !p.startsWith(`${VORLAGEN_ORDNER}/`);
  });
};

/**
 * Verschiebt ein Element um eine Stelle. Am Rand passiert NICHTS — bewusst
 * kein Umlauf ans andere Ende: ein Abschnitt, der beim Klick auf „hoch" ganz
 * nach unten springt, sieht aus wie ein Fehler.
 */
export const verschiebe = <T,>(liste: T[], index: number, richtung: -1 | 1): T[] => {
  const ziel = index + richtung;
  if (index < 0 || index >= liste.length || ziel < 0 || ziel >= liste.length) return liste;
  const neu = [...liste];
  [neu[index], neu[ziel]] = [neu[ziel], neu[index]];
  return neu;
};

export const ersetzeAn = <T,>(liste: T[], index: number, wert: T): T[] =>
  liste.map((e, i) => (i === index ? wert : e));

export const entferneAn = <T,>(liste: T[], index: number): T[] => liste.filter((_, i) => i !== index);

/** Trimmt Texte und wirft leere Antworten weg; leere Abschnitte/Fragen bleiben — die meldet pruefeEntwurf. */
export const bereinigeEntwurf = (e: UnterweisungEntwurf): UnterweisungEntwurf => ({
  ...e,
  titel: (e.titel || '').trim(),
  beschreibung: (e.beschreibung || '').trim() || null,
  inhalt: (e.inhalt || []).map((a) => ({
    ...a,
    titel: (a.titel || '').trim(),
    text: (a.text || '').trim(),
    bildPfad: a.bildPfad || null,
    videoPfad: a.videoPfad || null,
  })),
  fragen: (e.fragen || []).map((f) => ({
    ...f,
    frage: (f.frage || '').trim(),
    antworten: (f.antworten || [])
      .map((a) => ({ ...a, text: (a.text || '').trim(), richtig: a.richtig === true }))
      .filter((a) => a.text.length > 0),
    abschnittId: gueltigeAbschnittId(f.abschnittId, e.inhalt || []),
  })),
  bestehenProzent: klemme(Math.round(Number(e.bestehenProzent)), 0, 100, VORGABE_BESTEHEN_PROZENT),
  gueltigMonate: klemme(Math.round(Number(e.gueltigMonate)), 0, 600, VORGABE_GUELTIG_MONATE),
  dauerMin: e.dauerMin == null || !Number.isFinite(Number(e.dauerMin)) || Number(e.dauerMin) <= 0
    ? null
    : Math.round(Number(e.dauerMin)),
});

const klemme = (n: number, min: number, max: number, vorgabe: number): number =>
  Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : vorgabe;

/**
 * Was am Entwurf noch fehlt, bevor er AKTIV geschaltet werden darf. Leer =
 * alles in Ordnung. Speichern darf man IMMER (auch einen halben Entwurf);
 * diese Liste gilt nur fuer den Kundenlink.
 */
export const pruefeEntwurf = (e: UnterweisungEntwurf): string[] => {
  const fehler: string[] = [];
  if (!(e.titel || '').trim()) fehler.push('Ein Titel fehlt.');
  const abschnitte = e.inhalt || [];
  if (abschnitte.length === 0) fehler.push('Mindestens ein Abschnitt wird gebraucht.');
  abschnitte.forEach((a, i) => {
    if (!(a.text || '').trim() && !a.bildPfad && !a.videoPfad) fehler.push(`Abschnitt ${i + 1} hat weder Text noch Bild noch Video.`);
  });
  (e.fragen || []).forEach((f, i) => {
    if (!(f.frage || '').trim()) fehler.push(`Frage ${i + 1} hat keinen Text.`);
    const antworten = (f.antworten || []).filter((a) => (a.text || '').trim());
    if (antworten.length < 2) fehler.push(`Frage ${i + 1} braucht mindestens zwei Antworten.`);
    if (!antworten.some((a) => a.richtig)) fehler.push(`Frage ${i + 1} hat keine richtige Antwort.`);
  });
  const p = Number(e.bestehenProzent);
  if (!Number.isFinite(p) || p < 0 || p > 100) fehler.push('Die Bestehensgrenze muss zwischen 0 und 100 % liegen.');
  const m = Number(e.gueltigMonate);
  if (!Number.isFinite(m) || m < 0) fehler.push('Die Gültigkeit in Monaten darf nicht negativ sein.');
  return fehler;
};

/** Darf der Kurs aktiv geschaltet werden? Titel und mindestens ein Abschnitt, siehe pruefeEntwurf. */
export const darfAktivieren = (e: UnterweisungEntwurf): boolean => pruefeEntwurf(e).length === 0;

export const STATUS_LABEL: Record<UnterweisungStatus, string> = {
  entwurf: 'Entwurf',
  aktiv: 'Aktiv',
  archiviert: 'Archiviert',
};

/** Ist der Kundenlink gerade nutzbar? Token vorhanden, Kurs aktiv, nicht abgelaufen. */
export const linkIstAktiv = (u: Pick<SifaUnterweisung, 'status' | 'linkToken' | 'linkAktivBis'>, jetzt: Date = new Date()): boolean => {
  if (u.status !== 'aktiv' || !u.linkToken) return false;
  if (!u.linkAktivBis) return true;
  const bis = Date.parse(u.linkAktivBis);
  return Number.isNaN(bis) ? true : bis > jetzt.getTime();
};

/**
 * Der Kundenlink. `pathname` bleibt drin, weil die App nicht zwingend an der
 * Wurzel liegt; der Hash-Router (`#/unterweisung/<token>`) ist derselbe wie
 * bei `#/sign/<token>` in index.tsx.
 */
export const linkUrl = (origin: string, pathname: string, token: string): string =>
  `${origin}${pathname || '/'}#/unterweisung/${encodeURIComponent(token)}`;

/** Dateiname des QR-PNGs — ohne Zeichen, die ein Dateisystem stoeren. */
export const qrDateiname = (u: Pick<SifaUnterweisung, 'nummer' | 'titel'>): string => {
  const titel = (u.titel || 'Unterweisung')
    .normalize('NFKD')
    .replace(/[^\w\s-]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .slice(0, 40);
  return `QR_Unterweisung_${String(u.nummer).padStart(2, '0')}_${titel || 'Unterweisung'}.png`;
};

/** Vorgabe fuer den Aushang (Ueberschrift + Kurztext), vom Innendienst vor dem Druck aenderbar. */
export const aushangVorgabe = (u: Pick<SifaUnterweisung, 'titel' | 'dauerMin' | 'gueltigMonate'>): { ueberschrift: string; text: string } => {
  const dauer = u.dauerMin && u.dauerMin > 0 ? ` Die Unterweisung dauert etwa ${u.dauerMin} Minuten.` : '';
  return {
    ueberschrift: (u.titel || '').trim() || 'Unterweisung',
    text:
      `Bitte den QR-Code mit dem Smartphone scannen und die Unterweisung durchführen.${dauer}

` +
      'Am Ende Name, Geburtsdatum und E-Mail-Adresse eingeben und unterschreiben — das Zertifikat kommt per E-Mail.',
  };
};

/** Dateiname des Aushang-PDFs, gleiche Bereinigung wie beim QR-PNG. */
export const aushangDateiname = (u: Pick<SifaUnterweisung, 'nummer' | 'titel'>): string =>
  qrDateiname(u).replace(/^QR_/, 'Aushang_').replace(/\.png$/, '.pdf');

/** Sortierung der Ablage: neueste Nummer zuerst. */
export const sortiereUnterweisungen = (liste: SifaUnterweisung[]): SifaUnterweisung[] =>
  [...liste].sort((a, b) => (b.nummer || 0) - (a.nummer || 0));

// ─── Teilnahmen ─────────────────────────────────────────────────────────────

export interface TeilnahmenZaehler {
  gesamt: number;
  bestanden: number;
  nichtBestanden: number;
  /** Begonnen, aber nicht abgeschlossen. */
  offen: number;
  ohneMitarbeiter: number;
  /** Bestanden und noch nicht abgerechnet. */
  unabgerechnet: number;
}

export const istBestanden = (t: Pick<SifaTeilnahme, 'bestanden' | 'abgeschlossenAm'>): boolean =>
  !!t.abgeschlossenAm && t.bestanden === true;

export const istOffen = (t: Pick<SifaTeilnahme, 'abgeschlossenAm'>): boolean => !t.abgeschlossenAm;

/** Abrechenbar ist nur eine BESTANDENE, noch nicht gebuchte Teilnahme. */
export const istAbrechenbar = (t: Pick<SifaTeilnahme, 'bestanden' | 'abgeschlossenAm' | 'gebuchtAm'>): boolean =>
  istBestanden(t) && !t.gebuchtAm;

export const zaehleTeilnahmen = (teilnahmen: SifaTeilnahme[]): TeilnahmenZaehler => {
  const z: TeilnahmenZaehler = { gesamt: 0, bestanden: 0, nichtBestanden: 0, offen: 0, ohneMitarbeiter: 0, unabgerechnet: 0 };
  for (const t of teilnahmen || []) {
    z.gesamt += 1;
    if (istOffen(t)) z.offen += 1;
    else if (t.bestanden === true) z.bestanden += 1;
    else z.nichtBestanden += 1;
    if (!t.mitarbeiterId) z.ohneMitarbeiter += 1;
    if (istAbrechenbar(t)) z.unabgerechnet += 1;
  }
  return z;
};

/** Zaehler je Kurs — fuer die Karten der Ablage in einem Durchlauf. */
export const zaehleJeUnterweisung = (teilnahmen: SifaTeilnahme[]): Record<string, TeilnahmenZaehler> => {
  const gruppen: Record<string, SifaTeilnahme[]> = {};
  for (const t of teilnahmen || []) {
    (gruppen[t.unterweisungId] ||= []).push(t);
  }
  const out: Record<string, TeilnahmenZaehler> = {};
  for (const [id, liste] of Object.entries(gruppen)) out[id] = zaehleTeilnahmen(liste);
  return out;
};

export type TeilnahmenFilter = 'alle' | 'bestanden' | 'nicht_bestanden' | 'offen' | 'ohne_mitarbeiter' | 'unabgerechnet';

export const FILTER_LABEL: Record<TeilnahmenFilter, string> = {
  alle: 'Alle',
  bestanden: 'Bestanden',
  nicht_bestanden: 'Nicht bestanden',
  offen: 'Offen',
  ohne_mitarbeiter: 'Ohne Mitarbeiter',
  unabgerechnet: 'Nicht abgerechnet',
};

export const filtereTeilnahmen = (teilnahmen: SifaTeilnahme[], filter: TeilnahmenFilter): SifaTeilnahme[] => {
  const liste = teilnahmen || [];
  switch (filter) {
    case 'bestanden': return liste.filter(istBestanden);
    case 'nicht_bestanden': return liste.filter((t) => !istOffen(t) && t.bestanden !== true);
    case 'offen': return liste.filter(istOffen);
    case 'ohne_mitarbeiter': return liste.filter((t) => !t.mitarbeiterId);
    case 'unabgerechnet': return liste.filter(istAbrechenbar);
    default: return liste;
  }
};

/** Juengste zuerst: Abschluss, sonst Beginn. */
export const sortiereTeilnahmen = (teilnahmen: SifaTeilnahme[]): SifaTeilnahme[] =>
  [...(teilnahmen || [])].sort((a, b) => {
    const ta = Date.parse(a.abgeschlossenAm || a.begonnenAm || '') || 0;
    const tb = Date.parse(b.abgeschlossenAm || b.begonnenAm || '') || 0;
    return tb - ta;
  });

export type VersandStatus = 'gesendet' | 'fehler' | 'offen' | 'keins';

/**
 * Versandstand des Zertifikats. `keins` = es gibt (noch) kein Zertifikat —
 * nicht bestanden oder nicht abgeschlossen; da ist nichts zu senden.
 */
export const versandStatus = (t: Pick<SifaTeilnahme, 'bestanden' | 'abgeschlossenAm' | 'zertifikatGesendetAm' | 'zertifikatFehler'>): VersandStatus => {
  if (!istBestanden(t)) return 'keins';
  if (t.zertifikatGesendetAm) return 'gesendet';
  if (t.zertifikatFehler) return 'fehler';
  return 'offen';
};

export const VERSAND_LABEL: Record<VersandStatus, string> = {
  gesendet: 'Gesendet',
  fehler: 'Fehler',
  offen: 'Offen',
  keins: '—',
};

export const teilnehmerName = (t: Pick<SifaTeilnahme, 'vorname' | 'nachname'>): string =>
  [t.vorname, t.nachname].map((s) => (s || '').trim()).filter(Boolean).join(' ') || 'Unbekannt';

/** Ergebnis als „7 / 8 (88 %)"; ohne Punkte nur der Befund. */
export const ergebnisText = (t: Pick<SifaTeilnahme, 'punkte' | 'punkteMax' | 'bestanden' | 'abgeschlossenAm'>): string => {
  if (istOffen(t)) return 'offen';
  const befund = t.bestanden === true ? 'bestanden' : 'nicht bestanden';
  if (t.punkte == null || t.punkteMax == null || t.punkteMax <= 0) return befund;
  const prozent = Math.round((t.punkte / t.punkteMax) * 100);
  return `${t.punkte} / ${t.punkteMax} (${prozent} %) · ${befund}`;
};

/**
 * Die Teilnahmen, die nach einer Buchung als gebucht gelten. `gebuchtIds`
 * ist die RUECKGABE der DB-RPC — genau die Zeilen, die sie markiert hat
 * (nur solche mit `gebucht_am is null`). Eine id, die nicht darin steht,
 * bleibt unveraendert, auch wenn sie gewuenscht war.
 */
export const markiereLokalGebucht = (
  teilnahmen: SifaTeilnahme[],
  gebuchtIds: string[],
  positionId: string,
  gebuchtAm: string,
  mengeJeTeilnahme = 1,
): SifaTeilnahme[] => {
  const gebucht = new Set(gebuchtIds);
  return teilnahmen.map((t) => (gebucht.has(t.id) && !t.gebuchtAm
    ? { ...t, gebuchtAm, gebuchtePositionId: positionId, gebuchteMenge: mengeJeTeilnahme }
    : t));
};

// ─── Anzeige ────────────────────────────────────────────────────────────────

/** ISO-Datum oder Zeitstempel als TT.MM.JJJJ; ein reines Datum ohne Zeitzonenumrechnung. */
export const formatDatum = (iso: string | null | undefined): string => {
  const s = (iso || '').slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : (iso || '—');
};

/** Zeitstempel als TT.MM.JJJJ, HH:MM (Ortszeit). */
export const formatZeit = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const p = (n: number) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}, ${p(d.getHours())}:${p(d.getMinutes())}`;
};

/** Heute als ISO-Datum, ohne Zeitzonenversatz (toISOString kippt abends auf den Vortag). */
export const heuteIso = (jetzt: Date = new Date()): string => {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${jetzt.getFullYear()}-${p(jetzt.getMonth() + 1)}-${p(jetzt.getDate())}`;
};

export const gueltigkeitText = (monate: number): string =>
  monate > 0 ? `${monate} Monate` : 'ohne Ablauf';
